import { Link } from 'react-router-dom';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Chip } from '../../ui/index.ts';

/**
 * Readiness checklist — the wizard, in place (epic #1327 PR-4), demoted from
 * a page-bottom panel to the contents of the page-header readiness popover
 * (#1428).
 *
 * #1297's standalone activation wizard is deliberately not built. A wizard
 * that lives beside the pages it talks about goes stale the moment either
 * side moves; a checklist rendered from live readiness cannot. Every
 * unresolved row deep-links to the section that resolves it, and the anchors
 * it names are the ones the page declares in onboarding order.
 *
 * The bits themselves are now fetched ONCE by the page header, which owns the
 * chip that summarises them, and passed down. Before #1428 the header derived
 * "ready" from the targets table while this list read the readiness endpoint —
 * two sources for one claim, free to disagree on screen.
 */

/**
 * Where an unresolved item is fixed. In-page anchors are raw `href`s rather
 * than router `Link`s on purpose: React Router treats `#supply` as a route
 * change, which re-renders the page instead of scrolling within it.
 */
type ReadinessTarget =
  | { readonly kind: 'anchor'; readonly hash: string }
  | { readonly kind: 'route'; readonly to: string };

export interface ReadinessItem {
  readonly id: string;
  readonly labelKey: I18nKey;
  readonly hintKey: I18nKey;
  readonly ready: boolean;
  readonly target: ReadinessTarget;
}

/** Gateway-owned bits, as returned by the batched readiness read. */
export interface ReadinessGatewayBits {
  readonly binding: boolean;
  readonly probe: boolean;
  readonly cost: boolean;
}

/**
 * Three bits (binding / probe / cost) are gateway-owned facts about routing
 * and supply tables. Pricing and visibility are platform-owned and come from
 * the catalog record the page already holds — reading them again here would
 * only add a request that can disagree with the header.
 */
export function readinessItems({
  bits,
  priced,
  visible,
}: {
  readonly bits: ReadinessGatewayBits;
  /** Platform-owned: the SKU carries a usable tenant rate multiplier. */
  readonly priced: boolean;
  /** Platform-owned: organizations see this SKU without a per-org override. */
  readonly visible: boolean;
}): readonly ReadinessItem[] {
  return [
    {
      id: 'pricing',
      labelKey: 'sku_detail.checklist.pricing',
      hintKey: 'sku_detail.checklist.pricing_hint',
      ready: priced,
      target: { kind: 'anchor', hash: 'pricing' },
    },
    {
      id: 'binding',
      labelKey: 'sku_detail.checklist.binding',
      hintKey: 'sku_detail.checklist.binding_hint',
      ready: bits.binding,
      target: { kind: 'anchor', hash: 'supply' },
    },
    {
      id: 'probe',
      labelKey: 'sku_detail.checklist.probe',
      hintKey: 'sku_detail.checklist.probe_hint',
      ready: bits.probe,
      target: { kind: 'anchor', hash: 'supply' },
    },
    {
      id: 'cost',
      labelKey: 'sku_detail.checklist.cost',
      hintKey: 'sku_detail.checklist.cost_hint',
      ready: bits.cost,
      target: { kind: 'anchor', hash: 'supply' },
    },
    {
      id: 'visibility',
      labelKey: 'sku_detail.checklist.visibility',
      // Two hints, because this row is the only one whose unready state now
      // has an action attached. The old single string described the listed
      // state unconditionally, which read as a promise on every SKU created
      // since #1156 — none of which could reach it.
      hintKey: visible
        ? 'sku_detail.checklist.visibility_hint'
        : 'sku_detail.checklist.visibility_hint_private',
      ready: visible,
      // #1407: this row used to deep-link to `/crm`, a page with no visibility
      // control, and hint at a "=1 by default" state that no SKU created since
      // #1156 could reach — the column had no write path anywhere. Both are
      // now true: the shelf toggle lives in the pricing section of this page.
      target: { kind: 'anchor', hash: 'pricing' },
    },
  ];
}

function ItemRow({ item }: { readonly item: ReadinessItem }): JSX.Element {
  const t = useT();
  const linkClass =
    'inline-flex items-center gap-1 text-label text-signal transition-colors duration-120 hover:text-signal-bright';
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-soft px-3 py-2 last:border-b-0"
      data-testid={`sku-readiness-item-${item.id}`}
    >
      <Chip tone={item.ready ? 'ok' : 'warn'}>
        {t(item.ready ? 'sku_detail.checklist.ready' : 'sku_detail.checklist.not_ready')}
      </Chip>
      <span className="text-paper">{t(item.labelKey)}</span>
      <span className="min-w-0 flex-1 text-label text-paper-low">{t(item.hintKey)}</span>
      {item.ready ? null : item.target.kind === 'anchor' ? (
        <a
          href={`#${item.target.hash}`}
          className={linkClass}
          data-testid={`sku-readiness-link-${item.id}`}
        >
          {t('sku_detail.checklist.fix')}
        </a>
      ) : (
        <Link
          to={item.target.to}
          className={linkClass}
          data-testid={`sku-readiness-link-${item.id}`}
        >
          {t('sku_detail.checklist.fix')}
        </Link>
      )}
    </li>
  );
}

export function ReadinessChecklist({ items }: { readonly items: readonly ReadinessItem[] }): JSX.Element {
  return (
    <ul className="m-0 flex list-none flex-col p-0" data-testid="sku-readiness-checklist">
      {items.map((item) => (
        <ItemRow key={item.id} item={item} />
      ))}
    </ul>
  );
}
